import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  productionOrdersApi,
  type ProductionOrderLineInput,
} from "../../api/productionOrders";
import type { Order } from "../../api/orders";
import type { ApiError } from "../../api/client";
import { useToast } from "../Toast";
import { Spinner } from "../Loading";
import { formatNumber, intlLocale } from "../../lib/format";
import Ic from "../icons.jsx";

/**
 * Otvaranje narudžbenice za proizvodnju, u dijalogu.
 *
 * Obrazac je ranije stajao u uskoj koloni pored porudžbine: nazivi artikala su se prelamali u
 * pet redova, polja za količinu bila su šira od naziva, a dugmad su se gubila na dnu. Unos u
 * kom se ne vidi šta se unosi nije obrazac nego prepreka.
 *
 * Dijalog daje punu širinu, pa red stane u jedan red: naziv, naručeno, i koliko ide u
 * proizvodnju. Uz to nosi ono što je odluka, a ne kucanje — „sve" i „ništa" na jedan klik, i
 * zbir koji stalno kaže koliko se šalje.
 */
export function ProductionOrderModal({
  order, open, onClose, onCreated,
}: {
  order: Order;
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = intlLocale(i18n.resolvedLanguage);
  const toast = useToast();
  const [kolicine, setKolicine] = useState<Record<string, string>>({});
  const [rok, setRok] = useState("");
  const [napomena, setNapomena] = useState("");
  const [salje, setSalje] = useState(false);

  // Svako otvaranje kreće od čistog obrasca: zatečene količine od prošlog puta su tiha greška.
  useEffect(() => {
    if (!open) return;
    setKolicine({});
    setRok("");
    setNapomena("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const naEsc = (e: KeyboardEvent) => { if (e.key === "Escape" && !salje) onClose(); };
    document.addEventListener("keydown", naEsc);
    return () => document.removeEventListener("keydown", naEsc);
  }, [open, salje, onClose]);

  const broj = (s: string) => {
    const n = Number((s ?? "").trim().replace(",", "."));
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  const izabrano = useMemo(
    () => order.lines.filter((l) => broj(kolicine[l.id] ?? "") > 0).length,
    [order.lines, kolicine],
  );
  const ukupno = useMemo(
    () => order.lines.reduce((s, l) => s + broj(kolicine[l.id] ?? ""), 0),
    [order.lines, kolicine],
  );

  if (!open) return null;

  const sve = () =>
    setKolicine(Object.fromEntries(order.lines.map((l) => [l.id, String(l.quantity)])));
  const nista = () => setKolicine({});

  async function napravi() {
    const lines: ProductionOrderLineInput[] = [];
    for (const l of order.lines) {
      // Prazno polje znači da ta stavka ne ide u proizvodnju, jer se prodaje sa zalihe.
      const kolicina = broj(kolicine[l.id] ?? "");
      if (kolicina <= 0) continue;
      lines.push({
        productId: l.productId ?? undefined,
        freeTextDescription: l.productId ? undefined : (l.freeTextDescription ?? undefined),
        quantity: kolicina,
        unit: l.unit,
        sourceOrderLineId: l.id,
      });
    }
    if (lines.length === 0) {
      toast.error(t("production.pickAtLeastOne", "Unesite količinu za bar jednu stavku."));
      return;
    }
    setSalje(true);
    try {
      await productionOrdersApi.create({
        sourceOrderId: order.id,
        neededBy: rok || undefined,
        note: napomena.trim() || undefined,
        lines,
      });
      toast.success(t("production.created", "Narudžbenica je napravljena."));
      onCreated();
      onClose();
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setSalje(false);
    }
  }

  return (
    <div style={omotac} onClick={() => { if (!salje) onClose(); }}>
      <div
        className="modal-panel"
        style={ploca}
        role="dialog"
        aria-modal="true"
        aria-label={t("production.new", "Nova narudžbenica")}
        onClick={(e) => e.stopPropagation()}
      >
        <header style={zaglavlje}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, color: "var(--ink)" }}>
              {t("production.new", "Nova narudžbenica")}
            </h3>
            <div style={tiho}>
              {t("production.forOrder", "Za porudžbinu {{broj}}", { broj: order.orderNumber })}
            </div>
          </div>
          <button type="button" className="btn ghost sm" onClick={onClose} disabled={salje}
                  aria-label={t("common.close", "Zatvori")}>
            <Ic.x />
          </button>
        </header>

        <p style={uputstvo}>
          {t("production.pickHint",
             "Unesite koliko od svake stavke ide u proizvodnju. Ono što se prodaje sa zalihe ostavite prazno.")}
        </p>

        <div style={alatke}>
          <button type="button" className="btn sm" onClick={sve} disabled={salje}>
            {t("production.selectAll", "Sve u proizvodnju")}
          </button>
          <button type="button" className="btn sm" onClick={nista} disabled={salje}>
            {t("production.selectNone", "Očisti")}
          </button>
        </div>

        <table className="tbl" style={{ tableLayout: "fixed", width: "100%" }}>
          <thead>
            <tr>
              <th>{t("production.item", "Stavka")}</th>
              <th style={{ textAlign: "right", width: 130 }}>{t("production.ordered", "Naručeno")}</th>
              <th style={{ textAlign: "right", width: 170 }}>{t("production.toProduce", "U proizvodnju")}</th>
            </tr>
          </thead>
          <tbody>
            {order.lines.map((l) => {
              const uneto = broj(kolicine[l.id] ?? "");
              const previse = uneto > l.quantity;
              return (
                <tr key={l.id}>
                  <td style={{ paddingRight: 12 }}>{l.productName ?? l.freeTextDescription ?? "—"}</td>
                  <td style={celija}>
                    {/* Broj i jedinica idu u dva reda: jedinice ovde umeju da budu duge
                        („džambo džak 500Kg") i u jednom redu su ulazile u polje pored. */}
                    <div>{formatNumber(l.quantity, locale)}</div>
                    <div style={tiho}>{l.unit}</div>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <input
                        id={`proizvodnja-${l.id}`}
                        type="text"
                        inputMode="decimal"
                        className="inp sm"
                        aria-label={`${l.productName ?? l.freeTextDescription ?? ""} — ${t("production.toProduce", "U proizvodnju")}`}
                        style={{ textAlign: "right", width: 110,
                                 borderColor: previse ? "var(--warn)" : undefined }}
                        value={kolicine[l.id] ?? ""}
                        placeholder="0"
                        onChange={(e) => setKolicine((p) => ({ ...p, [l.id]: e.target.value }))}
                      />
                    </span>
                    {/* Više nego što je naručeno nije greška — proizvodi se i za zalihu — ali
                        jeste nešto što se radi namerno, pa se vidi. */}
                    {previse && (
                      <div style={{ ...tiho, color: "var(--warn)" }}>
                        {t("production.moreThanOrdered", "više nego naručeno")}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={dno}>
          <label htmlFor="proizvodnja-rok" style={oznaka}>{t("production.neededBy", "Treba do")}</label>
          <input id="proizvodnja-rok" type="date" className="inp sm" style={{ width: 170 }}
                 value={rok} onChange={(e) => setRok(e.target.value)} />
          <label htmlFor="proizvodnja-napomena" style={oznaka}>{t("production.note", "Napomena")}</label>
          <input id="proizvodnja-napomena" type="text" className="inp sm" style={{ flex: 1, minWidth: 180 }}
                 value={napomena} onChange={(e) => setNapomena(e.target.value)} />
        </div>

        <footer style={podnozje}>
          <span style={tiho}>
            {izabrano === 0
              ? t("production.nothingPicked", "Nijedna stavka nije izabrana")
              : t("production.picked", "{{count}} stavki · ukupno {{n}}",
                  { count: izabrano, n: formatNumber(ukupno, locale) })}
          </span>
          <span style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn" disabled={salje} onClick={onClose}>
              {t("common.cancel", "Odustani")}
            </button>
            <button type="button" className="btn primary" disabled={salje || izabrano === 0}
                    onClick={() => void napravi()}>
              {salje ? <Spinner size={13} /> : t("production.create", "Napravi")}
            </button>
          </span>
        </footer>
      </div>
    </div>
  );
}

const omotac: CSSProperties = {
  position: "fixed", inset: 0, background: "var(--overlay)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 20,
};
const ploca: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12,
  padding: 20, width: 720, maxWidth: "100%", maxHeight: "90dvh", overflowY: "auto",
  boxShadow: "var(--shadow-2)", display: "flex", flexDirection: "column", gap: 12,
};
const zaglavlje: CSSProperties = {
  display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12,
};
const tiho: CSSProperties = { fontSize: 12.5, color: "var(--ink-3)" };
const uputstvo: CSSProperties = { margin: 0, fontSize: 13, color: "var(--ink-2)", lineHeight: 1.45 };
const alatke: CSSProperties = { display: "flex", gap: 8 };
const celija: CSSProperties = {
  textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--ink-2)", lineHeight: 1.35,
};
const dno: CSSProperties = {
  display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
  paddingTop: 12, borderTop: "1px solid var(--border)",
};
const oznaka: CSSProperties = { fontSize: 12.5, color: "var(--ink-3)" };
const podnozje: CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
};
